import { gridsApi, itemsApi } from './api.js'
import { addBroadcastDays, broadcastDayKey, broadcastDayWindow } from './broadcast-day.js'
import { weekMondayISO } from './utils.js'
import { getServerNowMs } from '../hooks/useServerClock.js'
import { useStore } from '../store/index.js'
import type { ScheduleItem } from '../types/index.js'

/**
 * Annulation de la dernière action de grille.
 *
 * Le principe tient en une phrase : on photographie les grilles concernées
 * juste avant l'action, on les rephotographie juste après, et la différence
 * entre les deux photos **est** l'action. L'annuler, c'est jouer cette
 * différence à l'envers — supprimer ce qui est apparu, recréer ce qui a
 * disparu, remettre à sa place ce qui a bougé.
 *
 * Cette approche par photo, plutôt qu'une liste d'opérations inverses écrite à
 * la main pour chaque bouton, a une raison : côté serveur une seule action pose
 * souvent plusieurs éléments (un bloc multipartie, une boucle de rediffusion,
 * un modèle sur toute une semaine, un remplissage d'infoservice) et la réponse
 * de l'API ne dit pas toujours lesquels. La photo, elle, ne rate rien.
 *
 * Deux garde-fous, parce qu'une annulation fausse sur une grille d'antenne
 * coûte plus cher que pas d'annulation du tout :
 *
 * - si la photo « avant » échoue, aucune annulation n'est proposée — sans elle
 *   tout paraîtrait neuf, et l'annulation viderait la grille ;
 * - un élément n'est considéré comme créé par l'action que si son `createdAt`
 *   est postérieur au début de celle-ci. C'est ce qui protège des grilles
 *   révélées après coup (la rediffusion qui déborde sur la semaine suivante)
 *   et de ce qu'un autre poste aurait posé pendant ce temps-là.
 */

/** Statuts qu'on ne touche jamais : l'antenne a déjà tranché. */
const UNTOUCHABLE_STATUSES = ['ON_AIR', 'DONE', 'LOADING']

/** Tolérance sur l'horloge entre la photo « avant » et le `createdAt` serveur. */
const CLOCK_MARGIN_MS = 5_000

/** Champs qu'une annulation sait remettre en l'état sur un élément conservé. */
const RESTORABLE_FIELDS = [
  'startTime', 'durationSec', 'programId', 'mediaAssetId', 'liveSourceId',
  'liveStartMode', 'liveEndMode', 'liveHoldClipId', 'interludeClipId', 'sortOrder', 'notes',
] as const

type RestorableField = typeof RESTORABLE_FIELDS[number]

export interface GridUndoWindow {
  gridIds: string[]
  from:    string
  to:      string
}

export interface GridUndoChange {
  before: ScheduleItem
  after:  ScheduleItem
}

/** Ce qu'une action a changé, dans un format rejouable à l'envers. */
export interface GridUndoStep {
  created: ScheduleItem[]
  removed: ScheduleItem[]
  changed: GridUndoChange[]
}

export interface GridUndoReport {
  /** Éléments posés par l'action et retirés par l'annulation. */
  deleted:  number
  /** Éléments supprimés par l'action et remis en grille. */
  restored: number
  /** Éléments modifiés ou déplacés, remis dans leur état d'avant. */
  reverted: number
  /** Laissés tels quels : à l'antenne, déjà diffusés, ou impossibles à recréer. */
  skipped:  number
  /** Refusés par le serveur. */
  failed:   number
}

// ─── Fenêtre et lecture ───────────────────────────────────────────────────────

function unique(ids: Array<string | null | undefined>): string[] {
  return [...new Set(ids.filter((id): id is string => Boolean(id)))]
}

/**
 * Fenêtre photographiée : la semaine d'antenne de la date visée, étendue au
 * besoin jusqu'à `until` — une rediffusion peut viser la nuit suivante, un
 * modèle la semaine d'après.
 */
export function gridUndoWindow(gridIds: Array<string | null | undefined>, date: string, until?: string): GridUndoWindow {
  const monday  = weekMondayISO(date)
  const lastDay = broadcastDayKey(addBroadcastDays(monday, 6))
  const from    = broadcastDayWindow(monday).from
  const weekEnd = broadcastDayWindow(lastDay).to
  const untilEnd = until ? broadcastDayWindow(broadcastDayKey(new Date(until))).to : null
  const to = untilEnd && untilEnd > weekEnd ? untilEnd : weekEnd
  return { gridIds: unique(gridIds), from: from.toISOString(), to: to.toISOString() }
}

/** Photographie les grilles sur la fenêtre. Toute lecture ratée fait échouer. */
export async function readGridWindow(window: GridUndoWindow): Promise<ScheduleItem[]> {
  const batches = await Promise.all(
    window.gridIds.map(gridId => gridsApi.getItems(gridId, window.from, window.to)),
  )
  return batches.flat()
}

// ─── Différence entre deux photos ─────────────────────────────────────────────

function differs(before: ScheduleItem, after: ScheduleItem, field: RestorableField): boolean {
  const previous = before[field] ?? null
  const next     = after[field]  ?? null
  if (field === 'startTime') {
    return new Date(String(previous)).getTime() !== new Date(String(next)).getTime()
  }
  return previous !== next
}

/**
 * Ce qui sépare deux photos. `startedAt` (horloge serveur) est la borne qui
 * distingue « posé par l'action » de « déjà là » ; les statuts ne comptent pas
 * comme un changement, sinon un simple passage à l'antenne pendant la
 * manipulation ferait croire à une modification à annuler.
 */
export function diffGridItems(before: ScheduleItem[], after: ScheduleItem[], startedAt: number): GridUndoStep {
  const beforeById = new Map(before.map(item => [item.id, item]))
  const afterById  = new Map(after.map(item => [item.id, item]))

  const created = after.filter(item =>
    !beforeById.has(item.id)
    && new Date(item.createdAt).getTime() >= startedAt - CLOCK_MARGIN_MS)

  const removed = before.filter(item => !afterById.has(item.id))

  const changed: GridUndoChange[] = []
  for (const [id, previous] of beforeById) {
    const next = afterById.get(id)
    if (!next) continue
    if (RESTORABLE_FIELDS.some(field => differs(previous, next, field))) {
      changed.push({ before: previous, after: next })
    }
  }

  return { created, removed, changed }
}

export function isEmptyGridStep(step: GridUndoStep): boolean {
  return step.created.length === 0 && step.removed.length === 0 && step.changed.length === 0
}

/** Tous les éléments qu'une différence mentionne, dans un état ou dans l'autre. */
function gridStepItems(step: GridUndoStep): ScheduleItem[] {
  return [
    ...step.created,
    ...step.removed,
    ...step.changed.flatMap(change => [change.before, change.after]),
  ]
}

/**
 * Fenêtre couvrant tout ce qu'une différence a touché — celle qu'il faut
 * rephotographier pour enregistrer le rejeu de cette différence, à l'endroit
 * comme à l'envers.
 */
export function gridStepWindow(step: GridUndoStep, date: string): GridUndoWindow {
  const items  = gridStepItems(step)
  const latest = items.reduce<string | undefined>(
    (last, item) => (last === undefined || item.startTime > last ? item.startTime : last),
    undefined,
  )
  return gridUndoWindow(items.map(item => item.gridId), date, latest)
}

/** Nombre d'éléments qu'une annulation toucherait — de quoi rédiger la confirmation. */
export function gridStepSize(step: GridUndoStep): number {
  return step.created.length + step.removed.length + step.changed.length
}

/** Résumé en clair de ce que l'annulation va faire, pour la confirmation. */
export function describeGridStep(step: GridUndoStep): string {
  const parts: string[] = []
  if (step.created.length > 0) parts.push(`${step.created.length} élément(s) ajouté(s) seront retirés`)
  if (step.removed.length > 0) parts.push(`${step.removed.length} élément(s) supprimé(s) seront remis en grille`)
  if (step.changed.length > 0) parts.push(`${step.changed.length} élément(s) reviendront à leur état précédent`)
  return parts.join(' · ')
}

// ─── Rejouer la différence à l'envers ─────────────────────────────────────────

function isUntouchable(item: ScheduleItem): boolean {
  return UNTOUCHABLE_STATUSES.includes(item.status)
}

/**
 * Charge utile de recréation. Les parties d'un bloc multipartie sont écartées :
 * recréer la racine reconstruit le bloc entier côté serveur, les recréer une à
 * une le dédoublerait.
 */
function buildCreatePayload(item: ScheduleItem): Record<string, unknown> | null {
  const needsProgram = ['PROGRAM', 'AD_BREAK', 'JINGLE'].includes(item.itemType)
  if (needsProgram && !item.programId && !item.interludeClipId) return null
  if (item.itemType === 'LIVE' && !item.liveSourceId && !item.programId) return null
  if (item.itemType === 'INTERLUDE' && !item.interludeClipId) return null

  const payload: Record<string, unknown> = {
    gridId:      item.gridId,
    startTime:   item.startTime,
    durationSec: item.durationSec,
    itemType:    item.itemType,
    sortOrder:   item.sortOrder,
  }
  if (item.programId)       payload.programId       = item.programId
  if (item.mediaAssetId)    payload.mediaAssetId    = item.mediaAssetId
  if (item.liveSourceId)    payload.liveSourceId    = item.liveSourceId
  if (item.liveStartMode)   payload.liveStartMode   = item.liveStartMode
  if (item.liveEndMode)     payload.liveEndMode     = item.liveEndMode
  if (item.liveHoldClipId)  payload.liveHoldClipId  = item.liveHoldClipId
  if (item.interludeClipId) payload.interludeClipId = item.interludeClipId
  if (item.overrides)       payload.overrides       = item.overrides
  if (item.notes)           payload.notes           = item.notes.slice(0, 500)
  return payload
}

/** Ce qu'il faut repasser en PATCH pour retrouver l'état d'avant, hors position. */
function buildUpdatePayload(before: ScheduleItem, after: ScheduleItem): Record<string, unknown> | null {
  const payload: Record<string, unknown> = {}
  for (const field of RESTORABLE_FIELDS) {
    if (field === 'startTime') continue   // le déplacement passe par /move
    if (!differs(before, after, field)) continue
    const value = before[field]
    if (value === null && field !== 'liveHoldClipId') continue   // l'API n'efface pas ces liens
    payload[field] = value
  }
  return Object.keys(payload).length > 0 ? payload : null
}

/**
 * Joue la différence à l'envers. L'ordre compte : on libère d'abord la place en
 * retirant ce que l'action a posé, sinon la remise en grille de ce qu'elle avait
 * supprimé tomberait sur ses propres créations.
 */
export async function revertGridStep(step: GridUndoStep): Promise<GridUndoReport> {
  const report: GridUndoReport = { deleted: 0, restored: 0, reverted: 0, skipped: 0, failed: 0 }

  // 1. Retirer ce que l'action a créé, du plus tard au plus tôt.
  const creations = [...step.created]
    .sort((first, second) => new Date(second.startTime).getTime() - new Date(first.startTime).getTime())
  for (const item of creations) {
    if (isUntouchable(item)) { report.skipped += 1; continue }
    try {
      await itemsApi.delete(item.id)
      report.deleted += 1
    } catch (cause) {
      report.failed += 1
      console.error('[gridUndo] suppression impossible:', cause)
    }
  }

  // 2. Remettre dans leur état d'avant les éléments conservés.
  for (const { before, after } of step.changed) {
    if (isUntouchable(after)) { report.skipped += 1; continue }
    try {
      const patch = buildUpdatePayload(before, after)
      if (patch) await itemsApi.update(before.id, patch)
      if (differs(before, after, 'startTime')) await itemsApi.move(before.id, before.startTime)
      report.reverted += 1
    } catch (cause) {
      report.failed += 1
      console.error('[gridUndo] restauration impossible:', cause)
    }
  }

  // 3. Recréer ce que l'action avait supprimé.
  const removals = step.removed
    .filter(item => item.bundleId === null || (item.bundleOrder ?? 0) === 0)
    .sort((first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime())
  for (const item of removals) {
    const payload = buildCreatePayload(item)
    if (!payload) { report.skipped += 1; continue }
    try {
      await itemsApi.create(payload)
      report.restored += 1
    } catch (cause) {
      report.failed += 1
      console.error('[gridUndo] recréation impossible:', cause)
    }
  }

  return report
}

// ─── Enregistrement d'une action ──────────────────────────────────────────────

export interface RecordGridChangeOptions<T> {
  /** Ce que l'opérateur lira sur le bouton : « Annuler : chargement du modèle ». */
  label:   string
  /** Grilles connues avant l'action. */
  gridIds: Array<string | null | undefined>
  /** Journée d'antenne visée — sa semaine forme la fenêtre photographiée. */
  date:    string
  /** Fin de portée au-delà de la semaine, quand l'action peut déborder. */
  until?:  string
  /** Grilles que seule la réponse révèle (rediffusion sur la semaine suivante). */
  gridIdsFromResult?: (result: T) => Array<string | null | undefined>
}

/** Photo « avant » d'une action en cours, à refermer par `commitGridChange`. */
export interface GridChangeRecording {
  label:     string
  date:      string
  window:    GridUndoWindow
  startedAt: number
  /** `null` quand la photo a échoué : l'action se fera, sans annulation offerte. */
  before:    ScheduleItem[] | null
}

/**
 * Ouvre l'enregistrement d'une action : photographie les grilles concernées.
 * À utiliser quand l'action se disperse en plusieurs branches de code qui ne
 * peuvent pas tenir dans une seule fonction — sinon `recordGridChange` suffit.
 */
export async function beginGridChangeInWindow(
  label: string,
  date: string,
  window: GridUndoWindow,
): Promise<GridChangeRecording> {
  // Aucune grille connue à ce stade — l'action va la créer : la photo « avant »
  // est vide, et non ratée. Ce qui apparaîtra dans la grille née de l'action
  // sera bien né avec elle, le filtre sur `createdAt` s'en assure.
  let before: ScheduleItem[] | null = []
  if (window.gridIds.length > 0) {
    try {
      before = await readGridWindow(window)
    } catch (cause) {
      before = null
      console.error('[gridUndo] photo « avant » impossible, action non annulable:', cause)
    }
  }
  return { label, date, window, startedAt: getServerNowMs(), before }
}

export async function beginGridChange(
  options: Omit<RecordGridChangeOptions<unknown>, 'gridIdsFromResult'>,
): Promise<GridChangeRecording> {
  return beginGridChangeInWindow(
    options.label,
    options.date,
    gridUndoWindow(options.gridIds, options.date, options.until),
  )
}

/**
 * Referme l'enregistrement et rend la différence, sans rien empiler. C'est par
 * là que passe le rejeu d'une annulation : il a besoin de son propre inverse
 * pour alimenter la pile d'en face, pas d'une entrée de plus dans la sienne.
 */
export async function closeGridChange(
  recording: GridChangeRecording,
  extraGridIds: Array<string | null | undefined> = [],
): Promise<GridUndoStep | null> {
  if (!recording.before) return null
  try {
    const after = await readGridWindow({
      ...recording.window,
      gridIds: unique([...recording.window.gridIds, ...extraGridIds]),
    })
    const step = diffGridItems(recording.before, after, recording.startedAt)
    return isEmptyGridStep(step) ? null : step
  } catch (cause) {
    console.error('[gridUndo] photo « après » impossible, action non annulable:', cause)
    return null
  }
}

/**
 * Referme l'enregistrement d'une action d'opérateur : deuxième photo, différence,
 * empilement. `extraGridIds` accueille les grilles que seule la réponse du
 * serveur révèle.
 */
export async function commitGridChange(
  recording: GridChangeRecording,
  extraGridIds: Array<string | null | undefined> = [],
): Promise<void> {
  const step = await closeGridChange(recording, extraGridIds)
  if (step) {
    useStore.getState().pushGridUndoEntry({ label: recording.label, date: recording.date, step })
  }
}

/**
 * Exécute une action de grille en l'encadrant de deux photos, et empile de quoi
 * l'annuler. L'action est lancée quoi qu'il arrive : un échec de photo prive de
 * l'annulation, il ne prive jamais l'opérateur de son geste.
 */
export async function recordGridChange<T>(
  options: RecordGridChangeOptions<T>,
  run: () => Promise<T>,
): Promise<T> {
  const recording = await beginGridChange(options)
  const result = await run()
  await commitGridChange(recording, options.gridIdsFromResult?.(result) ?? [])
  return result
}

/**
 * Empile une différence déjà connue, sans photo. Réservé aux actions dont
 * l'inverse est exact et gratuit — le glisser-déposer, qui n'a pas à payer deux
 * lectures de semaine pour chaque déplacement.
 */
export function pushGridUndoStep(label: string, date: string, step: GridUndoStep): void {
  if (isEmptyGridStep(step)) return
  useStore.getState().pushGridUndoEntry({ label, date, step })
}
